/**
 * AB#22065 — the invoice trigger of a company: from which order stage and on
 * which days this customer is invoiced.
 *
 * The rule itself lives on the server (it also drives the finance queue). The
 * pure helpers here repeat only what the form needs while somebody is still
 * editing: the rule in words and the next invoice day. An invoice day on a
 * Saturday or Sunday moves to the Monday after, as on the server.
 */
import { ref } from 'vue';

import { showToast } from '@/composables/swal';
import { http, handleResponseException } from '@/interceptors/axiosRequestInterceptor';

export const INVOICE_TRIGGER_PERMISSION = 'manage-company-invoice-trigger';

export type InvoiceSchedule = 'immediate' | 'weekly' | 'monthly';

export interface InvoiceTrigger {
  /** An order status key, or null for any stage. */
  stage: string | null;
  /** With "shipped" or "completed": the tracking number must be entered first. */
  requires_tracking: boolean;
  schedule: InvoiceSchedule;
  /** Weekly: 1 (Monday) to 7. Monthly: 1 to 28, or LAST_DAY. */
  days: number[];
  note: string | null;
  is_default?: boolean;
  summary?: string;
}

export interface StageOption {
  value: string;
  label: string;
}

export interface OrderTriggerState {
  state: 'not_due' | 'ready' | 'overdue';
  due_on: string | null;
  reason: string | null;
  company_id: number | null;
  company_name: string | null;
  summary: string;
  note: string | null;
  is_default: boolean;
}

/** In a monthly schedule: the last day of the month, whatever its number. */
export const LAST_DAY = 31;
export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAY_NAMES = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const MONTH_DAYS = [...Array.from({ length: 28 }, (_, i) => i + 1), LAST_DAY];
export const SCHEDULES: { value: InvoiceSchedule; label: string }[] = [
  { value: 'immediate', label: 'Right away' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];
export const DEFAULT_TRIGGER: InvoiceTrigger = {
  stage: null,
  requires_tracking: false,
  schedule: 'monthly',
  days: [1, 15],
  note: null,
};

const ordinal = (day: number): string => {
  if (day === LAST_DAY) return 'last day';
  const tens = day % 100;
  const suffix = tens >= 11 && tens <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][day % 10] ?? 'th');

  return `${day}${suffix}`;
};

const joined = (parts: string[]): string =>
  parts.length < 2
    ? parts.join('')
    : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

/** The days a schedule can hold, sorted and without repeats. */
export const cleanDays = (schedule: InvoiceSchedule, days: number[]): number[] =>
  [...new Set(days.map(Number))]
    .filter((d) =>
      schedule === 'weekly'
        ? d >= 1 && d <= 7
        : schedule === 'monthly'
          ? (d >= 1 && d <= 28) || d === LAST_DAY
          : false
    )
    .sort((a, b) => a - b);

/** What stops this trigger from being saved, or null. */
export const triggerError = (trigger: InvoiceTrigger): string | null => {
  if (trigger.schedule === 'weekly' && !cleanDays('weekly', trigger.days).length) {
    return 'Choose at least one weekday.';
  }
  if (trigger.schedule === 'monthly' && !cleanDays('monthly', trigger.days).length) {
    return 'Choose at least one date of the month.';
  }
  if ((trigger.note ?? '').length > 255) return 'The note is too long (255 characters at most).';

  return null;
};

/** Stages that can carry the tracking condition (the server says the same). */
export const TRACKING_STAGES = ['shipped', 'completed'];

/** The value the stage dropdown carries for "<stage> — tracking number required". */
export const TRACKING_SUFFIX = '+tracking';

/**
 * The dropdown's entries: every stage the server lists, with "shipped" (and
 * "completed") offered twice, with and without the tracking condition. The
 * order's statuses are untouched; only the invoice trigger knows the difference.
 */
export const stageChoices = (
  stages: StageOption[],
  trackingStages: string[] = TRACKING_STAGES
): StageOption[] =>
  stages.flatMap((s) =>
    trackingStages.includes(s.value)
      ? [
          { value: `${s.value}${TRACKING_SUFFIX}`, label: `${s.label} — tracking number required` },
          { value: s.value, label: `${s.label} — tracking number not required` },
        ]
      : [s]
  );

/** The dropdown value for a trigger, and back. */
export const stageChoiceOf = (
  trigger: Pick<InvoiceTrigger, 'stage' | 'requires_tracking'>
): string =>
  trigger.stage === null
    ? ''
    : `${trigger.stage}${trigger.requires_tracking ? TRACKING_SUFFIX : ''}`;
export const applyStageChoice = (trigger: InvoiceTrigger, choice: string): void => {
  const tracking = choice.endsWith(TRACKING_SUFFIX);
  const stage = tracking ? choice.slice(0, -TRACKING_SUFFIX.length) : choice;
  trigger.stage = stage || null;
  trigger.requires_tracking = tracking && TRACKING_STAGES.includes(stage);
};

/** The agreement in one sentence, worded as the server words it. */
export const describeTrigger = (trigger: InvoiceTrigger, stages: StageOption[] = []): string => {
  const stage =
    trigger.stage === null
      ? null
      : (
          stages.find((s) => s.value === trigger.stage)?.label ?? trigger.stage.replace(/_/g, ' ')
        ).toLowerCase();
  const tracking = stage !== null && trigger.requires_tracking ? ' with a tracking number' : '';
  const what =
    stage === null
      ? 'every order not yet invoiced, whatever its stage'
      : `orders that have reached "${stage}"${tracking} and are not yet invoiced`;

  if (trigger.schedule === 'immediate') {
    return stage === null
      ? 'Invoice an order at any time, at any stage.'
      : `Invoice an order as soon as it reaches "${stage}"${tracking}.`;
  }
  if (trigger.schedule === 'weekly') {
    const days = cleanDays('weekly', trigger.days).map((d) => WEEKDAY_NAMES[d - 1]);

    return days.length ? `Every ${joined(days)}, invoice ${what}.` : 'Choose at least one weekday.';
  }
  const days = cleanDays('monthly', trigger.days).map(ordinal);

  return days.length
    ? `On the ${joined(days)} of each month, invoice ${what}.`
    : 'Choose at least one date of the month.';
};

/** Monday = 1 ... Sunday = 7. */
const isoWeekday = (d: Date): number => ((d.getDay() + 6) % 7) + 1;

const names = (trigger: InvoiceTrigger, d: Date): boolean => {
  if (trigger.schedule === 'weekly') return trigger.days.includes(isoWeekday(d));
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();

  return (
    trigger.days.includes(d.getDate()) || (trigger.days.includes(LAST_DAY) && d.getDate() === last)
  );
};

/**
 * The first invoice day on or after `from`, or null when the schedule has
 * none ("right away", or no day chosen yet).
 */
export const nextInvoiceDay = (trigger: InvoiceTrigger, from: Date = new Date()): Date | null => {
  if (trigger.schedule === 'immediate' || !trigger.days.length) return null;
  const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());

  // Two days back: a Saturday before `from` moves forward onto or past it.
  for (let i = -2; i <= 62; i++) {
    const named = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    if (!names(trigger, named)) continue;
    const weekday = isoWeekday(named);
    const moved =
      weekday >= 6
        ? new Date(named.getFullYear(), named.getMonth(), named.getDate() + (8 - weekday))
        : named;
    if (moved.getTime() >= start.getTime()) return moved;
  }

  return null;
};

/** "Mon 12 Oct" */
export const shortDay = (d: Date): string =>
  `${WEEKDAYS[isoWeekday(d) - 1]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;

/** "2026-10-12" → "Mon 12 Oct"; anything unreadable → ''. */
export const shortDayFromYmd = (ymd: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd ?? '');

  return m ? shortDay(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))) : '';
};

/** What is sent to the server: days only where the schedule has them. */
export const triggerPayload = (trigger: InvoiceTrigger) => ({
  stage: trigger.stage || null,
  requires_tracking:
    !!trigger.stage && TRACKING_STAGES.includes(trigger.stage) && trigger.requires_tracking,
  schedule: trigger.schedule,
  days: trigger.schedule === 'immediate' ? [] : cleanDays(trigger.schedule, trigger.days),
  note: (trigger.note ?? '').trim() || null,
});

const sameTrigger = (a: InvoiceTrigger, b: InvoiceTrigger): boolean =>
  JSON.stringify(triggerPayload(a)) === JSON.stringify(triggerPayload(b));

const fromServer = (raw: Partial<InvoiceTrigger> | null | undefined): InvoiceTrigger => ({
  stage: raw?.stage ?? null,
  requires_tracking: !!raw?.requires_tracking,
  schedule: (raw?.schedule ?? DEFAULT_TRIGGER.schedule) as InvoiceSchedule,
  days: Array.isArray(raw?.days) ? raw.days.map(Number) : [...DEFAULT_TRIGGER.days],
  note: raw?.note ?? null,
  is_default: !!raw?.is_default,
});

/** The Invoice trigger section of a company: load, edit, save. */
export function useCompanyInvoiceTrigger() {
  const trigger = ref<InvoiceTrigger>({ ...DEFAULT_TRIGGER, days: [...DEFAULT_TRIGGER.days] });
  const saved = ref<InvoiceTrigger>({ ...DEFAULT_TRIGGER, days: [...DEFAULT_TRIGGER.days] });
  const stages = ref<StageOption[]>([]);
  const updatedBy = ref<string | null>(null);
  const updatedAt = ref<string | null>(null);
  const isDefault = ref(true);
  const loading = ref(false);
  const loaded = ref(false);
  const saving = ref(false);

  const apply = (result: Record<string, unknown> | undefined): void => {
    const next = fromServer(result?.trigger as Partial<InvoiceTrigger>);
    trigger.value = { ...next, days: [...next.days] };
    saved.value = { ...next, days: [...next.days] };
    isDefault.value = !!next.is_default;
    if (Array.isArray(result?.stages)) stages.value = result.stages as StageOption[];
    updatedBy.value = (result?.updated_by as string | null) ?? null;
    updatedAt.value = (result?.updated_at as string | null) ?? null;
  };

  const load = async (companyId: number): Promise<void> => {
    loading.value = true;
    try {
      const response = await http.get(`companies/${companyId}/invoice-trigger`);
      apply(response?.data?.result);
      loaded.value = true;
    } catch (e) {
      loaded.value = false;
      handleResponseException(e);
    } finally {
      loading.value = false;
    }
  };

  const save = async (companyId: number): Promise<boolean> => {
    const error = triggerError(trigger.value);
    if (error) {
      showToast(error, 'error');

      return false;
    }
    saving.value = true;
    try {
      const response = await http.put(
        `companies/${companyId}/invoice-trigger`,
        triggerPayload(trigger.value)
      );
      apply(response?.data?.result);
      showToast('Invoice trigger saved.', 'success');

      return true;
    } catch (e) {
      handleResponseException(e);

      return false;
    } finally {
      saving.value = false;
    }
  };

  const isDirty = (): boolean => !sameTrigger(trigger.value, saved.value);

  return {
    trigger,
    stages,
    updatedBy,
    updatedAt,
    isDefault,
    loading,
    loaded,
    saving,
    load,
    save,
    isDirty,
  };
}

/**
 * Where the items of one order stand against the trigger of the company billed
 * for them. Read by the Create Invoice step; a failure here never stops anyone
 * invoicing, it only means no notice is shown.
 */
export function useOrderInvoiceTrigger() {
  const items = ref<Record<string, OrderTriggerState>>({});

  const load = async (orderId: number | string | null | undefined): Promise<void> => {
    if (!orderId) return;
    try {
      const response = await http.get(`customer-orders/${orderId}/invoice-trigger`);
      items.value = (response?.data?.result?.items ?? {}) as Record<string, OrderTriggerState>;
    } catch {
      items.value = {};
    }
  };

  const stateFor = (itemId: number | string | null | undefined): OrderTriggerState | null =>
    itemId === null || itemId === undefined ? null : (items.value[String(itemId)] ?? null);

  return { items, load, stateFor };
}

/** The line above Create Invoice: the agreement, then why it is not due yet. */
export const triggerNotice = (state: OrderTriggerState | null): string =>
  state
    ? [state.summary, state.state === 'not_due' ? state.reason : null].filter(Boolean).join(' ')
    : '';

/** The words of the confirmation shown when invoicing before the trigger. */
export const earlyInvoiceWarning = (
  state: OrderTriggerState | null
): { title: string; text: string } | null =>
  state && state.state === 'not_due'
    ? {
        title: 'Invoice before the agreed trigger?',
        text: [state.reason, 'You can still create the invoice now.'].filter(Boolean).join(' '),
      }
    : null;
